import React, {useState} from 'react';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {Stage} from '../Stage';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import {NoActionStage as NoActionStageType} from "../../../api/application/getApplicationDetails";
import {useMutation} from "@apollo/client";
import {CHANGE_APPLICATION_STAGE_STATUS} from "../../../api/application/changeApplicationStageStatus";
import {getIsCompleted, getSubtitle} from "./util";
import LoadingButton from "@mui/lab/LoadingButton";

type NoActionStageProps = {
    nextStageTitle?: string,
    refetch: () => void,
    stage: NoActionStageType,
}

export const NoActionStage = (props: NoActionStageProps) => {
    const {
        nextStageTitle,
        refetch,
        stage,
    } = props

    const [isModalOpen, setIsModalOpen] = useState(false)

    const [changeStatus, {loading}] = useMutation(CHANGE_APPLICATION_STAGE_STATUS)

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const onChangeStatus = async () => {
        const {errors} = await changeStatus({variables: {applicationStageId: stage.id, newStatus: 'APPROVED'}})

        if (errors === undefined || errors.length === 0) {
            setIsModalOpen(false)

            await refetch()
        }
    }

    const isCompleted = getIsCompleted(stage)

    const subtitle = getSubtitle(stage)

    return (
        <>
            <Stage
                icon={isCompleted ? <CheckCircleOutlineIcon color="success" /> : <AccessTimeIcon color="warning"/>}
                id={stage.id}
                subTitle={subtitle}
                title={stage.title}
            >
                <Typography>
                    {stage.description}
                </Typography>

                <Stack direction="row" gap="1rem" justifyContent="flex-end" marginTop="1rem">
                    <Button
                        disabled={isCompleted}
                        color="success"
                        variant="contained"
                        onClick={() => setIsModalOpen(true)}
                    >
                        Proceed to Next Stage
                    </Button>
                </Stack>

                <Dialog
                    fullScreen={fullScreen}
                    open={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                >
                    <DialogTitle>
                        Progress Application
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {nextStageTitle
                                ? (<span>The next stage is <strong>{nextStageTitle}.</strong> </span>)
                                : (<span>This is the last stage. </span>)
                            }
                            Are you sure you'd like to progress this application to the next stage?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            color="error"
                            onClick={() => setIsModalOpen(false)}
                            variant="contained"
                            disabled={loading}
                        >
                            No, Go Back
                        </Button>
                        <LoadingButton
                            color="success"
                            onClick={() => onChangeStatus()}
                            variant="contained"
                            disabled={loading}
                            loading={loading}
                        >
                            Yes, Proceed
                        </LoadingButton>
                    </DialogActions>
                </Dialog>
            </Stage>
        </>
    )
}
